import { createClient } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';

// One client per page. Shared with non-hook code (lib/stripe.ts) that needs the
// current session token, so both paths read the same auto-refreshed session.
let singleton: ReturnType<typeof createClient> | null = null;
export const getSupabaseClient = () => {
  if (!singleton) {
    singleton = createClient(
      process.env.REACT_APP_SUPABASE_URL as string,
      process.env.REACT_APP_SUPABASE_ANON_KEY as string,
      {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
        },
      }
    );
  }
  return singleton;
};

export const useSupabaseClient = () => {
  return useQuery({
    queryKey: ['useSupabaseClient'],
    queryFn: async () => {
      console.log('🔄 SUPABASE: Creating client...');
      
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      console.log('🔑 SUPABASE: Environment state:', {
        hasUrl: !!supabaseUrl,
        hasKey: !!supabaseKey,
      });

      if (!supabaseUrl || !supabaseKey) {
        console.log('❌ SUPABASE: Missing configuration');
        throw new Error('Supabase configuration missing. Please check your .env file.');
      }

      const supabase = getSupabaseClient();

      // Check initial auth state
      const { data: { session }, error } = await supabase.auth.getSession();
      console.log('🔍 SUPABASE: Initial auth state:', {
        hasSession: !!session,
        sessionError: error?.message,
        accessToken: session?.access_token ? 'present' : 'missing',
        refreshToken: session?.refresh_token ? 'present' : 'missing',
      });

      // Subscribe to auth changes
      supabase.auth.onAuthStateChange((event, session) => {
        console.log('👀 SUPABASE: Auth state changed:', {
          event,
          hasSession: !!session,
          accessToken: session?.access_token ? 'present' : 'missing',
          refreshToken: session?.refresh_token ? 'present' : 'missing',
        });
      });

      return supabase;
    },
    staleTime: Infinity,
  });
}; 